import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ onFileSelect }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      onFileSelect(files?.[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      onFileSelect(files?.[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef?.current?.click();
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-medium p-6 border border-border">
      <h2 className="text-lg font-semibold text-foreground mb-4">Upload Receipt</h2>
      
      {/* Drag & Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          isDragging 
            ? 'border-primary bg-primary/5 scale-[1.02]' 
            : 'border-border hover:border-primary/50 hover:bg-muted/30'
        }`}
      >
        <div className="flex flex-col items-center gap-4">
          <div className={`p-4 rounded-full transition-colors ${
            isDragging ? 'bg-primary/20' : 'bg-muted'
          }`}>
            <Icon 
              name="Upload" 
              className={`w-10 h-10 transition-colors ${
                isDragging ? 'text-primary' : 'text-muted-foreground'
              }`} 
            />
          </div>
          
          <div>
            <p className="text-base font-medium text-foreground mb-1">
              {isDragging ? 'Drop receipt image here' : 'Drag & drop receipt image'}
            </p>
            <p className="text-sm text-muted-foreground">
              or choose an option below
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md mt-2">
            <Button
              onClick={handleBrowseClick}
              variant="outline"
              className="flex-1"
            >
              <Icon name="FolderOpen" className="w-4 h-4 mr-2" />
              Browse Files
            </Button>
            
            <Button
              onClick={handleCameraClick}
              variant="outline"
              className="flex-1"
            >
              <Icon name="Camera" className="w-4 h-4 mr-2" />
              Take Photo
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-2">
            Supports JPG, PNG, WEBP • Max 10MB
          </p>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileInputChange}
          className="hidden"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      {/* Tips Section */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">Tips for Best OCR Results</p>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Ensure receipt is well-lit and in focus</li>
              <li>• Capture the entire receipt including merchant name and total</li>
              <li>• Avoid shadows and glare on the receipt</li>
              <li>• Straighten the receipt for optimal text recognition</li>
              <li>• Higher resolution images produce better results</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadZone;